import React from "react";
import { X, Loader2, Check, Copy, ExternalLink, Send, Plus, Download, IndianRupee } from "lucide-react";
import { CurrencyIcon } from "./CurrencyIcon";

/**
 * PaymentLinkModal — extracted from ChatsPage.jsx (iter 156.2, Feb 2026, Yash).
 *
 * Multi-provider payment-link / QR-code generator. Razorpay (INR only),
 * Stripe (overseas FX), and PayPal (gated by `providersConfig`).
 *
 * Why extracted: ChatsPage was 2,544 lines. This modal alone was ~225 of
 * them and is fully self-contained (props in, callbacks out).
 */
export const PaymentLinkModal = ({
  open,
  onClose,
  // form state
  paymentProvider,
  setPaymentProvider,
  paymentAmount,
  setPaymentAmount,
  paymentCurrency,
  setPaymentCurrency,
  paymentDesc,
  setPaymentDesc,
  payCustomerName,
  setPayCustomerName,
  payCustomerPhone,
  setPayCustomerPhone,
  payCustomerEmail,
  setPayCustomerEmail,
  // provider config (Stripe / PayPal enable gating)
  providersConfig,
  // result state
  creatingPayment,
  generatedLink,
  setGeneratedLink,
  qrBase64,
  setQrBase64,
  sendingQR,
  // handlers
  onCreate,
  onDownloadQR,
  onSendQR,
  onCopyLink,
  onSendLinkInChat,
}) => {
  if (!open) return null;
  const stripeReady = providersConfig?.stripe?.enabled ?? true;
  const paypalReady = providersConfig?.paypal?.enabled ?? false;

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="payment-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm animate-scale-in">
        <div className="flex items-center justify-between p-4 border-b border-[--border]">
          <div className="flex items-center gap-2">
            <span className="w-8 h-8 rounded-lg bg-green-50 text-green-600 flex items-center justify-center"><IndianRupee size={16} /></span>
            <h2 className="text-sm font-semibold" style={{ fontFamily: "Playfair Display, serif" }}>Payment Link</h2>
          </div>
          <button onClick={onClose} className="btn-icon"><X size={18} /></button>
        </div>
        <div className="p-4 space-y-3">
          {!generatedLink ? (
            <>
              {/* Provider selector — Stripe vs Razorpay. PayPal slot is
                  pre-disabled until user provides Sandbox creds. */}
              <div>
                <label className="text-[11px] font-medium text-[--text-muted] block mb-1.5">Payment provider</label>
                <div className="grid grid-cols-3 gap-1.5" data-testid="payment-provider-toggle">
                  <button
                    type="button"
                    onClick={() => { setPaymentProvider("razorpay"); setPaymentCurrency("INR"); }}
                    className={`py-2 rounded-lg text-[10px] font-bold uppercase tracking-wider border transition-all ${
                      paymentProvider === "razorpay"
                        ? "bg-blue-600 text-white border-blue-600 shadow-sm"
                        : "bg-white text-slate-700 border-slate-200 hover:border-blue-300"
                    }`}
                    data-testid="payment-provider-razorpay-btn"
                  >
                    Razorpay
                    <span className="block text-[8px] font-normal opacity-80">India · UPI / Cards</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      if (!stripeReady) return;
                      setPaymentProvider("stripe");
                      if (paymentCurrency === "INR") setPaymentCurrency("USD");
                    }}
                    disabled={!stripeReady}
                    className={`py-2 rounded-lg text-[10px] font-bold uppercase tracking-wider border transition-all ${
                      !stripeReady
                        ? "bg-slate-50 text-slate-400 border-slate-200 cursor-not-allowed"
                        : paymentProvider === "stripe"
                          ? "bg-violet-600 text-white border-violet-600 shadow-sm"
                          : "bg-white text-slate-700 border-slate-200 hover:border-violet-300"
                    }`}
                    title={
                      !stripeReady
                        ? (providersConfig?.stripe?.reason || "Stripe is in test mode and disabled for safety. Add a sk_live_... key to enable.")
                        : "Stripe — overseas cards & wallets"
                    }
                    data-testid="payment-provider-stripe-btn"
                  >
                    Stripe
                    <span className="block text-[8px] font-normal opacity-80">
                      {!stripeReady ? "Awaiting live key" : "Overseas · Cards / Wallets"}
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      if (!paypalReady) return;
                      setPaymentProvider("paypal");
                      if (paymentCurrency === "INR") setPaymentCurrency("USD");
                    }}
                    disabled={!paypalReady}
                    className={`py-2 rounded-lg text-[10px] font-bold uppercase tracking-wider border transition-all ${
                      !paypalReady
                        ? "bg-slate-50 text-slate-400 border-slate-200 cursor-not-allowed"
                        : paymentProvider === "paypal"
                          ? "bg-[#003087] text-white border-[#003087] shadow-sm"
                          : "bg-white text-slate-700 border-slate-200 hover:border-[#003087]"
                    }`}
                    title={
                      !paypalReady
                        ? (providersConfig?.paypal?.reason || "PayPal credentials not configured.")
                        : "PayPal — global wallet & cards"
                    }
                    data-testid="payment-provider-paypal-btn"
                  >
                    PayPal
                    <span className="block text-[8px] font-normal opacity-80">
                      {!paypalReady ? "Awaiting credentials" : "Global · Wallet / Cards"}
                    </span>
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div className="col-span-2">
                  <label className="text-[11px] font-medium text-[--text-muted] block mb-1">Amount *</label>
                  <div className="flex items-center gap-2 bg-[--bg-cream] border border-[--border] rounded-lg px-3 py-2">
                    <CurrencyIcon currency={paymentCurrency} size={14} className="text-[--text-muted]" />
                    <input type="number" value={paymentAmount} onChange={(e) => setPaymentAmount(e.target.value)} placeholder="0.00" min="1"
                      className="flex-1 text-sm bg-transparent outline-none placeholder:text-[--text-muted]" data-testid="payment-amount-input" autoFocus />
                  </div>
                </div>
                <div>
                  <label className="text-[11px] font-medium text-[--text-muted] block mb-1">Currency</label>
                  <select
                    value={paymentCurrency}
                    onChange={(e) => {
                      const cur = e.target.value;
                      setPaymentCurrency(cur);
                      // Auto-switch provider when the currency clearly belongs
                      // to the other payment rail. Saves the agent a click.
                      // BUT only flip TO Stripe if Stripe is enabled — never
                      // silently route the agent to a disabled rail.
                      if (cur === "INR" && paymentProvider !== "razorpay") setPaymentProvider("razorpay");
                      if (cur !== "INR" && paymentProvider === "razorpay" && stripeReady) setPaymentProvider("stripe");
                    }}
                    className="w-full bg-[--bg-cream] border border-[--border] rounded-lg px-2 py-2 text-sm outline-none" data-testid="payment-currency-select">
                    {/* Yash, Apr 29 2026 — currencies are filtered by selected provider:
                        - Razorpay → INR only (Indian gateway, doesn't accept FX)
                        - Stripe / PayPal → all our 17 supported non-INR currencies
                        INR is hidden when Stripe/PayPal is the active provider. */}
                    {paymentProvider === "razorpay" ? (
                      <option value="INR">INR — Indian Rupee (₹)</option>
                    ) : (
                      [
                        { c: "USD", n: "US Dollar ($)" },
                        { c: "EUR", n: "Euro (€)" },
                        { c: "GBP", n: "British Pound (£)" },
                        { c: "AUD", n: "Australian Dollar (A$)" },
                        { c: "CAD", n: "Canadian Dollar (C$)" },
                        { c: "SGD", n: "Singapore Dollar (S$)" },
                        { c: "AED", n: "UAE Dirham" },
                        { c: "CHF", n: "Swiss Franc" },
                        { c: "NZD", n: "New Zealand Dollar (NZ$)" },
                        { c: "HKD", n: "Hong Kong Dollar (HK$)" },
                        { c: "JPY", n: "Japanese Yen (¥)" },
                        { c: "CNY", n: "Chinese Yuan (¥)" },
                        { c: "MYR", n: "Malaysian Ringgit" },
                        { c: "THB", n: "Thai Baht (฿)" },
                        { c: "KRW", n: "South Korean Won (₩)" },
                        { c: "RUB", n: "Russian Ruble (₽)" },
                        { c: "NPR", n: "Nepalese Rupee" },
                      ].map((o) => (
                        <option key={o.c} value={o.c}>{o.c} — {o.n}</option>
                      ))
                    )}
                  </select>
                </div>
              </div>
              <div>
                <label className="text-[11px] font-medium text-[--text-muted] block mb-1">Description</label>
                <input type="text" value={paymentDesc} onChange={(e) => setPaymentDesc(e.target.value)} placeholder="Payment for order..."
                  className="w-full bg-[--bg-cream] border border-[--border] rounded-lg px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-[--accent] placeholder:text-[--text-muted]" data-testid="payment-desc-input" />
              </div>
              <div>
                <label className="text-[11px] font-medium text-[--text-muted] block mb-1">Customer name</label>
                <input type="text" value={payCustomerName} onChange={(e) => setPayCustomerName(e.target.value)} placeholder="Jane Customer"
                  className="w-full bg-[--bg-cream] border border-[--border] rounded-lg px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-[--accent]" data-testid="payment-name-input" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[11px] font-medium text-[--text-muted] block mb-1">Phone</label>
                  <input type="tel" value={payCustomerPhone} onChange={(e) => setPayCustomerPhone(e.target.value)} placeholder="+91..."
                    className="w-full bg-[--bg-cream] border border-[--border] rounded-lg px-3 py-2 text-sm outline-none" data-testid="payment-phone-input" />
                </div>
                <div>
                  <label className="text-[11px] font-medium text-[--text-muted] block mb-1">Email <span className="text-[--text-muted] font-normal">(optional)</span></label>
                  <input type="email" value={payCustomerEmail} onChange={(e) => setPayCustomerEmail(e.target.value)} placeholder="Optional — phone alone is fine"
                    className="w-full bg-[--bg-cream] border border-[--border] rounded-lg px-3 py-2 text-sm outline-none" data-testid="payment-email-input" />
                </div>
              </div>
              <button onClick={onCreate} disabled={creatingPayment || !paymentAmount}
                className="w-full btn-primary py-2.5 justify-center" data-testid="generate-payment-btn">
                {creatingPayment ? <Loader2 size={14} className="animate-spin" /> : <><CurrencyIcon currency={paymentCurrency} size={14} /> Generate Payment Link</>}
              </button>
            </>
          ) : (
            <div className="space-y-3">
              <div className="p-3 bg-green-50 border border-green-200 rounded-xl text-center">
                <Check size={24} className="mx-auto text-green-600 mb-1" />
                <p className="text-sm font-semibold text-green-800">Link Created!</p>
                <p className="text-xs text-green-600 mt-1" data-testid="generated-link-summary">
                  {(generatedLink.currency || paymentCurrency)} {Number(generatedLink.amount || 0).toLocaleString()}
                  {generatedLink.provider === "stripe" && (
                    <span className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded-full text-[8px] font-extrabold uppercase tracking-wider bg-violet-100 text-violet-700 border border-violet-200">Stripe</span>
                  )}
                  {generatedLink.provider === "paypal" && (
                    <span className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded-full text-[8px] font-extrabold uppercase tracking-wider bg-[#003087] text-white border border-[#003087]">PayPal</span>
                  )}
                  {generatedLink.provider !== "stripe" && generatedLink.provider !== "paypal" && (
                    <span className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded-full text-[8px] font-extrabold uppercase tracking-wider bg-blue-100 text-blue-700 border border-blue-200">Razorpay</span>
                  )}
                </p>
              </div>
              {/* QR Code */}
              {qrBase64 && (
                <div className="flex flex-col items-center bg-white border border-[--border] rounded-xl p-3">
                  <img src={`data:image/png;base64,${qrBase64}`} alt="Payment QR Code" className="w-40 h-40 rounded-lg" data-testid="payment-qr-image" />
                  <p className="text-[10px] text-[--text-muted] mt-2">Scan to pay</p>
                  <div className="flex gap-2 mt-2">
                    <button onClick={onDownloadQR} className="flex items-center gap-1 text-[10px] text-[--text-secondary] hover:text-[--accent] transition-colors" data-testid="download-qr-btn">
                      <Download size={11} /> Download
                    </button>
                    <button onClick={onSendQR} disabled={sendingQR} className="flex items-center gap-1 text-[10px] text-green-600 hover:text-green-700 font-medium transition-colors" data-testid="send-qr-whatsapp-btn">
                      {sendingQR ? <Loader2 size={11} className="animate-spin" /> : <Send size={11} />} Send QR via WhatsApp
                    </button>
                  </div>
                </div>
              )}
              {/* Payment link URL */}
              <div className="flex items-center gap-2 bg-[--bg-cream] border border-[--border] rounded-lg px-3 py-2">
                <p className="flex-1 text-xs text-[--text-secondary] truncate">{generatedLink.short_url}</p>
                <button onClick={() => onCopyLink(generatedLink.short_url)} className="btn-icon w-7 h-7" title="Copy" data-testid="copy-payment-link"><Copy size={13} /></button>
                <a href={generatedLink.short_url} target="_blank" rel="noopener noreferrer" className="btn-icon w-7 h-7" title="Open" data-testid="open-payment-link"><ExternalLink size={13} /></a>
              </div>
              <div className="flex gap-2">
                <button onClick={() => onSendLinkInChat(generatedLink.short_url)} className="flex-1 btn-primary py-2 justify-center text-xs" data-testid="send-payment-link-btn">
                  <Send size={12} /> Send Link in Chat
                </button>
                <button onClick={() => { setGeneratedLink(null); setPaymentAmount(""); setPaymentDesc(""); setQrBase64(null); }} className="btn-secondary py-2 text-xs" data-testid="new-payment-link-btn">
                  <Plus size={12} /> New
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
